import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { groupIntoPeriods, formatSpanExact, type Locale } from '@monrdv/shared';
import { request } from '../lib/api.js';
import { RecordLink } from '../components/RecordLink.js';
import { usePreferences } from '../lib/preferences.js';

/**
 * What has happened at a place.
 *
 * A house outlasts the people in it. Somebody sells, the new owner calls about the same heat
 * pump in the same basement, and the six visits made for the previous owner are filed under a
 * customer nobody thinks to look up. This screen is the answer to "what do we already know
 * about this unit" — asked from the doorstep as often as from the office.
 *
 * The distinction it works hardest to keep visible is **dwelling versus building**. 411
 * dwellings here are shared by more than one customer, which is the case worth having. But the
 * largest building holds 82 customers across 42 recorded units, and presenting those as one
 * address's history would be worse than useless — it would look authoritative and be wrong. So
 * the two are separate views, the heading always says which one is on screen, and where the
 * data cannot tell them apart the screen says that too rather than picking.
 */

type Occupant = {
  id: string;
  name: string;
  status: string | null;
  firstSeen: string | null;
  lastSeen: string | null;
  jobs: number;
};

type Visit = {
  id: string;
  at: string;
  status: string;
  number: string | null;
  jobId: string;
  jobNumber: string | null;
  jobTitle: string | null;
  customer: { id: string; name: string } | null;
  technician: string | null;
  services: string | null;
};

type Place = {
  id: string;
  scope: 'place' | 'building';
  line1: string;
  line2: string | null;
  unit: string | null;
  city: string;
  region: string;
  postalCode: string | null;
  note: string | null;
  building: { customers: number; units: number; unitsUnrecorded: boolean };
  occupants: Occupant[];
  visits: Visit[];
  /** Work recorded at this address that no one was ever scheduled to attend. */
  unvisited: Array<{
    jobId: string;
    jobNumber: string | null;
    jobTitle: string | null;
    createdAt: string | null;
    customer: { id: string; name: string } | null;
  }>;
  invoices: Array<{
    id: string;
    number: string | null;
    issuedOn: string | null;
    total: string | null;
    status: string;
    customerName: string | null;
  }>;
};

export function AddressHistoryScreen() {
  const { t, i18n } = useTranslation();
  const locale = (i18n.language.startsWith('en') ? 'en' : 'fr') as Locale;
  const { addressId } = useParams({ from: '/addresses/$addressId' });
  const search = useSearch({ from: '/addresses/$addressId' });
  const navigate = useNavigate();
  usePreferences();

  const scope = search.scope ?? 'place';

  const place = useQuery({
    queryKey: ['address', addressId, scope],
    queryFn: ({ signal }) => request<Place>(`/addresses/${addressId}?scope=${scope}`, { signal }),
  });

  if (place.isPending) return <p className="screen__loading">{t('common.loading')}</p>;
  if (place.isError || !place.data) return <div className="notice notice--error">{t('errors.generic')}</div>;

  const data = place.data;
  const periods = groupIntoPeriods(data.visits, (v) => v.at, locale);

  return (
    <div className="screen place">
      <header className="place__head">
        <div>
          <h1 className="place__title">
            {/* The unit first, because that is what identifies the home to the person reading. */}
            {data.unit && <span className="place__unit">{t('address.unit', { unit: data.unit })}</span>}
            {stripUnit(data.line1)}
          </h1>
          <p className="place__where">
            {data.city}, {data.region}
            {data.postalCode && <span className="tnum"> {data.postalCode}</span>}
          </p>
          {data.note && <p className="place__note">{data.note}</p>}
        </div>

        {/*
          Only offered when there is genuinely something else to look at. A detached house has
          no building view worth having, and a control that leads somewhere identical is noise.
        */}
        {data.building.customers > data.occupants.length && (
          <div className="segmented" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={scope === 'place'}
              className={`segmented__option${scope === 'place' ? ' segmented__option--on' : ''}`}
              onClick={() => navigate({ to: '.', search: { scope: undefined } as never, replace: true })}
            >
              {t('address.thisHome')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={scope === 'building'}
              className={`segmented__option${scope === 'building' ? ' segmented__option--on' : ''}`}
              onClick={() => navigate({ to: '.', search: { scope: 'building' } as never, replace: true })}
            >
              {t('address.wholeBuilding', { count: data.building.customers })}
            </button>
          </div>
        )}
      </header>

      {/*
        The honest caveat. Several customers at one civic address with no unit recorded between
        them is an apartment block whose units nobody wrote down — not a home that changed hands
        many times. Saying so is the difference between a useful screen and a confident lie.
      */}
      {data.building.unitsUnrecorded && (
        <div className="notice notice--caution">{t('address.unitsUnrecorded')}</div>
      )}

      <section className="place__occupants">
        <h2>{scope === 'building' ? t('address.customersHere') : t('address.occupants')}</h2>
        <ul className="place__people">
          {data.occupants.map((person, index) => (
            <li key={person.id} className="place__person">
              <RecordLink kind="customer" id={person.id} className="place__personname">
                {person.name}
              </RecordLink>
              {/* The most recently active is the one to call. Said, not merely implied by order. */}
              {index === 0 && scope === 'place' && data.occupants.length > 1 && (
                <span className="place__current">{t('address.mostRecent')}</span>
              )}
              <span className="place__span tnum">{span(person.firstSeen, person.lastSeen, locale)}</span>
              <span className="place__jobs">{t('address.jobCount', { count: person.jobs })}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="place__work">
        <h2>{t('address.workHere')}</h2>
        {periods.length === 0 && <p className="screen__empty">{t('address.noWork')}</p>}

        {periods.map((period) => (
          <div key={period.label + period.start.toISOString()} className="period">
            <h3 className="period__head" title={formatSpanExact(period.start, period.end, locale)}>
              <span className="period__label">{period.label}</span>
              <span className="period__rule" aria-hidden="true" />
              <span className="period__count">{period.items.length}</span>
            </h3>

            <ul className="period__items">
              {period.items.map((visit) => (
                <li key={visit.id} className="period__item">
                  <span className="tnum period__when">{day(visit.at, locale)}</span>
                  <span className="period__what">
                    <RecordLink kind="job" id={visit.jobId}>
                      {visit.jobNumber ? `#${visit.jobNumber}` : t('job.one')}
                    </RecordLink>
                    {visit.services && <span className="period__services"> · {visit.services}</span>}
                  </span>
                  {/*
                    Whose job it was. On a place shared over time this is the column that tells
                    you the visit belonged to somebody who no longer lives here.
                  */}
                  {visit.customer && (
                    <RecordLink kind="customer" id={visit.customer.id} className="period__who">
                      {visit.customer.name}
                    </RecordLink>
                  )}
                  {visit.technician && <span className="period__tech">{visit.technician}</span>}
                </li>
              ))}
            </ul>
          </div>
        ))}

        {/*
          Work with an address but no visit — a quote that never became one, a job closed
          without anybody being scheduled. Kept out of the dated periods because it has no date
          of attendance to sort by, and listing it under a month it did not happen in would be
          inventing a fact.
        */}
        {data.unvisited.length > 0 && (
          <div className="period">
            <h3 className="period__head">
              <span className="period__label">{t('address.neverVisited')}</span>
              <span className="period__rule" aria-hidden="true" />
              <span className="period__count">{data.unvisited.length}</span>
            </h3>
            <ul className="period__items">
              {data.unvisited.map((job) => (
                <li key={job.jobId} className="period__item">
                  <span className="tnum period__when">{job.createdAt ? day(job.createdAt, locale) : '—'}</span>
                  <span className="period__what">
                    <RecordLink kind="job" id={job.jobId}>
                      {job.jobNumber ? `#${job.jobNumber}` : t('job.one')}
                    </RecordLink>
                    {job.jobTitle && <span className="period__services"> · {job.jobTitle}</span>}
                  </span>
                  {job.customer && (
                    <RecordLink kind="customer" id={job.customer.id} className="period__who">
                      {job.customer.name}
                    </RecordLink>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </div>
  );
}

/** The line without its leading unit, which the heading shows separately. */
function stripUnit(line1: string): string {
  const match = /^[0-9A-Za-z]+-(\d+ .*)$/.exec(line1);
  return match ? match[1]! : line1;
}

function span(first: string | null, last: string | null, locale: Locale): string {
  if (!first && !last) return '—';
  const format = new Intl.DateTimeFormat(locale === 'en' ? 'en-CA' : 'fr-CA', { month: 'short', year: 'numeric' });
  const from = first ? format.format(new Date(first)) : '?';
  const to = last ? format.format(new Date(last)) : '?';
  return from === to ? from : `${from} – ${to}`;
}

function day(iso: string, locale: Locale): string {
  return new Intl.DateTimeFormat(locale === 'en' ? 'en-CA' : 'fr-CA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(iso));
}
